import { Download, Copy, CheckCircle2, Flag } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { ReportCueDialog } from "./support/ReportCueDialog";

function fmtTime(sec) {
  const s = Math.max(0, sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  const ms = Math.floor((s - Math.floor(s)) * 1000);
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(ss).padStart(2,"0")}.${String(ms).padStart(3,"0")}`;
}

export default function SubtitlePreview({ job, onDownload }) {
  const [copied, setCopied] = useState(null);
  // Reports one already-stored cue to support. Read-only: the subtitle is
  // never regenerated, re-timed or re-segmented by this action.
  const [reportCue, setReportCue] = useState(null);

  if (!job) {
    return (
      <div className="border border-zinc-800 bg-zinc-900/40 min-h-[540px] flex items-center justify-center p-8 text-center">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Step 02 — Output</div>
          <div className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white mb-2">
            Your subtitles will land here
          </div>
          <p className="text-zinc-500 max-w-sm mx-auto text-sm">
            Choose your input, source and target languages — then hit generate.
          </p>
        </div>
      </div>
    );
  }

  const handleCopy = async (fmt) => {
    await navigator.clipboard.writeText(job[fmt]);
    setCopied(fmt);
    toast.success(`${fmt.toUpperCase()} copied to clipboard`);
    setTimeout(() => setCopied(null), 1600);
  };

  return (
    <div className="border border-zinc-800 bg-zinc-900/40" data-testid="subtitle-preview-panel">
      <div className="p-6 md:p-8 border-b border-zinc-800 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" /> Ready
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">
            {job.segments.length} subtitle segments
          </h2>
          <div data-testid="subtitle-language-pair" className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-2">
            {job.resolved_source_language || job.detected_language || job.source_language} → {job.target_language}
            {job.source_language === "auto" && <span data-testid="subtitle-detected-label"> · Auto-detected</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => onDownload("srt")}
            data-testid="download-srt-btn"
            className="h-10 bg-white text-zinc-950 hover:bg-zinc-200 rounded-none font-semibold transition-colors duration-200"
          >
            <Download className="w-4 h-4 mr-2" /> .srt
          </Button>
          <Button
            onClick={() => onDownload("vtt")}
            data-testid="download-vtt-btn"
            className="h-10 bg-white text-zinc-950 hover:bg-zinc-200 rounded-none font-semibold transition-colors duration-200"
          >
            <Download className="w-4 h-4 mr-2" /> .vtt
          </Button>
          <Button
            onClick={() => handleCopy("srt")}
            data-testid="copy-srt-btn"
            className="h-10 border border-zinc-800 bg-transparent hover:bg-zinc-800 text-zinc-300 hover:text-white rounded-none transition-colors duration-200"
          >
            <Copy className="w-4 h-4 mr-2" /> {copied === "srt" ? "Copied" : "Copy SRT"}
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[500px]">
        <div className="divide-y divide-zinc-800">
          {job.segments.map((seg, i) => {
            const idx = seg.index ?? (i + 1);
            return (
            <div key={`seg-${idx}-${i}`} data-testid="subtitle-segment" className="p-5 md:px-8 hover:bg-zinc-900/60 transition-colors duration-150 group">
              <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  <div>#{String(idx).padStart(3,"0")}</div>
                  <div className="mt-1 text-zinc-400">{fmtTime(seg.start)}</div>
                  <div className="text-zinc-600">→ {fmtTime(seg.end)}</div>
                  <button
                    type="button"
                    onClick={() => setReportCue({
                      index: idx,
                      start: seg.start,
                      end: seg.end,
                      source_text: seg.source_text,
                      translated_text: seg.translated_text,
                    })}
                    data-testid={`report-cue-btn-${idx}`}
                    title="Report a problem with this subtitle"
                    className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-600 hover:text-red-300 md:opacity-0 md:group-hover:opacity-100 transition-all duration-150"
                  >
                    <Flag className="w-3 h-3" /> Report
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="text-zinc-500 text-sm leading-relaxed font-mono" data-testid="segment-source">
                    {seg.source_text}
                  </div>
                  <div className="text-white text-base leading-relaxed whitespace-pre-line" data-testid="segment-translation">
                    {seg.translated_text}
                  </div>
                  {(seg.speaker || seg.cps != null || (seg.qc_flags && seg.qc_flags.length > 0)) && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      {seg.speaker && (
                        <span className="text-[10px] font-mono uppercase tracking-widest border border-zinc-800 text-zinc-400 px-1.5 py-0.5">
                          {seg.speaker}
                        </span>
                      )}
                      {seg.cps != null && (
                        <span
                          className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${
                            seg.cps > 17
                              ? "border-amber-500/40 text-amber-300"
                              : "border-zinc-800 text-zinc-500"
                          }`}
                          title="Characters per second"
                        >
                          {seg.cps} CPS
                        </span>
                      )}
                      {seg.qc_flags?.map((f) => (
                        <span
                          key={f}
                          className="text-[10px] font-mono uppercase tracking-widest border border-red-500/40 text-red-300 px-1.5 py-0.5"
                        >
                          {f.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </ScrollArea>
      <ReportCueDialog
        open={!!reportCue}
        onOpenChange={(v) => { if (!v) setReportCue(null); }}
        jobId={job.job_id}
        cue={reportCue}
      />
    </div>
  );
}
